import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, ShieldAlert, Trash2, Upload } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import type {
  ReferenceDocumentDetail,
  ReferenceDocumentScope,
  ReferenceDocumentSupportedTypes,
  ReferenceDocumentUpdate,
  ReferenceDocumentView,
  StrategyDetail,
  StrategySummary,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";

/**
 * The reference-document library for a strategy.
 *
 * <p>Upload a document, then decide three separate things about it: whether the
 * agent may read it while drafting, which phases that applies to, and whether
 * anything that uses it has to be approved by a human first. Those are
 * deliberately not one switch — see the note by the approval toggle.
 *
 * <p>Nothing is on by default. A freshly uploaded document is inert until
 * somebody enables it, which is also when the injection screen runs.
 */
export default function DocumentsPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [strategyId, setStrategyId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  useEffect(() => {
    if (strategyId) return;
    const first = strategiesQ.data?.[0];
    if (first) setStrategyId(first.id);
  }, [strategiesQ.data, strategyId]);

  const base = `/api/v1/strategies/${strategyId}/reference-documents`;

  const detailQ = useQuery({
    queryKey: ["strategy-detail", strategyId],
    enabled: !!strategyId,
    queryFn: () => api.get<StrategyDetail>(`/api/v1/strategies/${strategyId}`),
  });

  const documentsQ = useQuery({
    queryKey: ["reference-documents", strategyId],
    enabled: !!strategyId,
    queryFn: () => api.get<ReferenceDocumentView[]>(base),
  });

  const supportedQ = useQuery({
    queryKey: ["reference-documents-types", strategyId],
    enabled: !!strategyId,
    staleTime: Infinity,
    queryFn: () => api.get<ReferenceDocumentSupportedTypes>(`${base}/supported-types`),
  });

  const selectedQ = useQuery({
    queryKey: ["reference-document", selectedId],
    enabled: !!selectedId && !!strategyId,
    queryFn: () => api.get<ReferenceDocumentDetail>(`${base}/${selectedId}`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["reference-documents", strategyId] });
    queryClient.invalidateQueries({ queryKey: ["reference-document", selectedId] });
  };

  const updateM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ReferenceDocumentUpdate }) =>
      api.patch<ReferenceDocumentView>(`${base}/${id}`, patch),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (e) => setActionError(apiErrorMessage(e, "could not save the change")),
  });

  const releaseM = useMutation({
    mutationFn: (id: string) => api.post<ReferenceDocumentView>(`${base}/${id}/release`),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (e) => setActionError(apiErrorMessage(e, "could not release the document")),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.del<void>(`${base}/${id}`),
    onSuccess: (_r, id) => {
      setActionError(null);
      if (selectedId === id) setSelectedId(null);
      invalidate();
    },
    onError: (e) => setActionError(apiErrorMessage(e, "could not delete the document")),
  });

  const documents = documentsQ.data ?? [];
  const phases = detailQ.data?.phases ?? [];
  const accept = supportedQ.data?.extensions.join(",") ?? ".pdf";
  const selected = useMemo(
    () => documents.find((d) => d.id === selectedId) ?? null,
    [documents, selectedId],
  );

  async function uploadFiles(files: File[]) {
    if (!strategyId || files.length === 0) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await api.upload<ReferenceDocumentView>(base, form);
      }
      invalidate();
    } catch (e) {
      setUploadError(apiErrorMessage(e, "upload failed"));
    } finally {
      setIsUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    void uploadFiles(Array.from(e.dataTransfer.files));
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Documents</h1>
          <p className="text-sm text-[var(--fg-muted)]">
            Documents the agent can read while drafting, and fill in from a conversation.
            Uploads are stored as extracted text — the original file is not kept.
          </p>
        </div>
        <label className="text-sm">
          <span className="sr-only">Strategy</span>
          <select
            aria-label="Strategy"
            className="border rounded px-2 py-1 text-sm"
            value={strategyId ?? ""}
            onChange={(e) => {
              setStrategyId(e.target.value);
              setSelectedId(null);
            }}
          >
            {(strategiesQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </header>

      <Card>
        <CardTitle>Upload</CardTitle>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`mt-2 rounded border border-dashed p-6 text-center text-sm ${
            dragging ? "border-blue-500 bg-blue-50" : "border-[var(--border)]"
          }`}
        >
          <Upload className="mx-auto mb-2 h-5 w-5 text-[var(--fg-muted)]" aria-hidden />
          <p>Drop a document here, or</p>
          <button
            type="button"
            className="mt-2 rounded border px-3 py-1"
            disabled={!strategyId || isUploading}
            onClick={() => fileRef.current?.click()}
          >
            {isUploading ? "Uploading…" : "Choose a file"}
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={accept}
            className="hidden"
            aria-label="Document file"
            onChange={(e) => void uploadFiles(Array.from(e.target.files ?? []))}
          />
          <p className="mt-2 text-xs text-[var(--fg-muted)]">
            Accepted: {accept.split(",").join(" ")}. A scanned image PDF has no text to
            extract and will be refused — run it through OCR first.
          </p>
        </div>
        {uploadError && (
          <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
            {uploadError}
          </p>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <Card>
          <CardTitle>Library</CardTitle>
          {documentsQ.isLoading ? (
            <p className="text-sm text-[var(--fg-muted)]">Loading…</p>
          ) : documents.length === 0 ? (
            <p className="text-sm text-[var(--fg-muted)]">
              No documents yet. Upload one above.
            </p>
          ) : (
            <ul className="divide-y">
              {documents.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(d.id)}
                    aria-current={selectedId === d.id}
                    className={`flex w-full items-start gap-2 px-1 py-2 text-left text-sm ${
                      selectedId === d.id ? "bg-zinc-50" : ""
                    }`}
                  >
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fg-muted)]" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{d.title}</span>
                      <span className="block text-xs text-[var(--fg-muted)]">
                        {d.sourceFilename} · {d.extractedChars.toLocaleString()} chars ·{" "}
                        {formatDateTime(d.createdAt)}
                      </span>
                      <span className="mt-1 flex flex-wrap gap-1">
                        <Pill on={d.enabled} onLabel="Enabled" offLabel="Disabled" />
                        {d.requiresApproval && <Tag tone="warn">Approval required</Tag>}
                        {d.fillable && <Tag>Fillable</Tag>}
                        {d.status === "QUARANTINED" && <Tag tone="danger">Quarantined</Tag>}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {selected ? (
          <DocumentSettings
            document={selected}
            extractedText={selectedQ.data?.extractedText ?? null}
            phases={phases.map((p) => ({ slug: p.slug, name: p.name }))}
            error={actionError}
            saving={updateM.isPending}
            onSave={(patch) => updateM.mutate({ id: selected.id, patch })}
            onRelease={() => releaseM.mutate(selected.id)}
            onDelete={() => deleteM.mutate(selected.id)}
          />
        ) : (
          <Card>
            <p className="text-sm text-[var(--fg-muted)]">
              Select a document to configure how the agent may use it.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function Pill({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] ${
        on ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"
      }`}
    >
      {on ? onLabel : offLabel}
    </span>
  );
}

function Tag({ children, tone }: { children: ReactNode; tone?: "warn" | "danger" }) {
  const cls =
    tone === "danger"
      ? "bg-rose-50 text-rose-700"
      : tone === "warn"
        ? "bg-amber-50 text-amber-700"
        : "bg-zinc-100 text-zinc-600";
  return <span className={`rounded px-1.5 py-0.5 text-[11px] ${cls}`}>{children}</span>;
}

interface SettingsProps {
  document: ReferenceDocumentView;
  extractedText: string | null;
  phases: { slug: string; name: string }[];
  error: string | null;
  saving: boolean;
  onSave: (patch: ReferenceDocumentUpdate) => void;
  onRelease: () => void;
  onDelete: () => void;
}

function DocumentSettings({
  document,
  extractedText,
  phases,
  error,
  saving,
  onSave,
  onRelease,
  onDelete,
}: SettingsProps) {
  const [scope, setScope] = useState<ReferenceDocumentScope>(document.scope);
  const [phaseSlugs, setPhaseSlugs] = useState<string[]>(document.phaseSlugs);

  useEffect(() => {
    setScope(document.scope);
    setPhaseSlugs(document.phaseSlugs);
  }, [document.id, document.scope, document.phaseSlugs]);

  const quarantined = document.status === "QUARANTINED";
  const scopedToNothing = scope === "SELECTED_PHASES" && phaseSlugs.length === 0;

  function togglePhase(slug: string) {
    const next = phaseSlugs.includes(slug)
      ? phaseSlugs.filter((s) => s !== slug)
      : [...phaseSlugs, slug];
    setPhaseSlugs(next);
    onSave({ scope, phaseSlugs: next });
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <CardTitle>{document.title}</CardTitle>
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-[var(--danger)]"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden /> Delete
        </button>
      </div>

      {quarantined && (
        <div role="alert" className="mt-2 rounded border border-[var(--danger)] p-2 text-sm">
          <p className="flex items-center gap-1 font-medium text-[var(--danger)]">
            <ShieldAlert className="h-4 w-4" aria-hidden /> Quarantined
          </p>
          <p className="mt-1 text-[var(--fg-muted)]">
            {document.statusReason ??
              "The injection screen flagged this document's text."}
          </p>
          <p className="mt-1 text-[var(--fg-muted)]">
            A reference document is put into the agent's system prompt as written, so
            instructions hidden in one carry real authority. Read the text below before
            releasing it.
          </p>
          <button type="button" className="mt-2 rounded border px-2 py-1 text-xs" onClick={onRelease}>
            I have read it — release
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
          {error}
        </p>
      )}

      <div className="mt-3 space-y-3 text-sm">
        <Toggle
          label="Enabled"
          hint="The agent may use this document. Turning it on runs the injection screen."
          checked={document.enabled}
          disabled={saving || quarantined || (scopedToNothing && !document.enabled)}
          onChange={(v) => onSave({ enabled: v, scope, phaseSlugs })}
        />

        <fieldset className="rounded border p-2">
          <legend className="px-1 text-xs text-[var(--fg-muted)]">Applies to</legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name={`scope-${document.id}`}
              checked={scope === "ALL_PHASES"}
              onChange={() => {
                setScope("ALL_PHASES");
                onSave({ scope: "ALL_PHASES" });
              }}
            />
            All phases ({phases.length})
          </label>
          <label className="mt-1 flex items-center gap-2">
            <input
              type="radio"
              name={`scope-${document.id}`}
              checked={scope === "SELECTED_PHASES"}
              onChange={() => {
                setScope("SELECTED_PHASES");
                onSave({ scope: "SELECTED_PHASES", phaseSlugs });
              }}
            />
            Selected phases
          </label>
          {scope === "SELECTED_PHASES" && (
            <div className="mt-2 flex flex-wrap gap-2 pl-6">
              {phases.map((p) => (
                <label key={p.slug} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={phaseSlugs.includes(p.slug)}
                    onChange={() => togglePhase(p.slug)}
                  />
                  {p.name}
                </label>
              ))}
              {scopedToNothing && (
                <p className="w-full text-xs text-amber-700">
                  Pick at least one phase — as configured this document would never be used.
                </p>
              )}
            </div>
          )}
        </fieldset>

        <Toggle
          label="Use as drafting context"
          hint="Put this document's text into the prompt when the agent writes an email in scope."
          checked={document.useAsPromptContext}
          disabled={saving}
          onChange={(v) => onSave({ useAsPromptContext: v })}
        />

        <Toggle
          label="Fillable"
          hint="Allow the agent to fill this document in from the conversation and produce a draft for review."
          checked={document.fillable}
          disabled={saving}
          onChange={(v) => onSave({ fillable: v })}
        />

        <Toggle
          label="Approval required"
          hint={
            document.requiresApproval
              ? "Any email that uses, names or quotes this document is held for a human, even in unattended mode."
              : "Emails using this document follow the strategy's normal sending mode."
          }
          checked={document.requiresApproval}
          disabled={saving}
          confirm={
            document.requiresApproval
              ? "Turn approval off for this document? Emails that use or quote it will then be able to send unattended."
              : undefined
          }
          onChange={(v) => onSave({ requiresApproval: v })}
        />

        <label className="block">
          <span className="text-xs text-[var(--fg-muted)]">
            Context budget (tokens) — how much of the document reaches the prompt
          </span>
          <input
            type="number"
            min={100}
            max={20000}
            defaultValue={document.contextTokenBudget}
            className="mt-1 w-32 rounded border px-2 py-1"
            onBlur={(e) => {
              const v = Number(e.target.value);
              if (Number.isFinite(v) && v !== document.contextTokenBudget) {
                onSave({ contextTokenBudget: v });
              }
            }}
          />
        </label>
      </div>

      <details className="mt-4">
        <summary className="cursor-pointer text-sm">
          Extracted text ({document.extractedChars.toLocaleString()} characters)
        </summary>
        <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-zinc-50 p-2 text-xs">
          {extractedText ?? "Loading…"}
        </pre>
      </details>
    </Card>
  );
}

interface ToggleProps {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  /** Shown before switching off a safety-relevant setting. */
  confirm?: string;
  onChange: (value: boolean) => void;
}

function Toggle({ label, hint, checked, disabled, confirm, onChange }: ToggleProps) {
  return (
    <label className="flex items-start gap-2">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.checked;
          if (!next && confirm && !window.confirm(confirm)) return;
          onChange(next);
        }}
      />
      <span>
        <span className="font-medium">{label}</span>
        <span className="block text-xs text-[var(--fg-muted)]">{hint}</span>
      </span>
    </label>
  );
}
